import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { BingAttribute, BingAttributeMetaData, ValueType } from '@vendasta/listing-products';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BingAttributeFull, CreateAttribute } from './attribute-interface';

@Component({
  selector: 'forms-bing-attributes',
  templateUrl: './bing-attributes.component.html',
  styleUrls: ['./bing-attributes.component.scss'],
})
export class BingAttributesComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input() form: UntypedFormGroup;
  @Input() appearance: 'outline' | 'standard' = 'standard';
  @Input() metadata$: Observable<BingAttributeMetaData[]>;
  @Input() attributes$: Observable<BingAttribute[]>;
  @Input() categoryLength: number;
  @Input() editDisabled = false;
  @Output() navigateToPrimaryInfoTab = new EventEmitter();

  orderedInputAttributes$$ = new BehaviorSubject<BingAttributeFull[]>([]);

  fullAttributes$$ = new BehaviorSubject<BingAttributeFull[]>([]);
  filteredAttributes$$ = new BehaviorSubject<BingAttributeFull[]>([]);
  autocompleteControl = new FormControl('');

  constructor(private fb: UntypedFormBuilder) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  autoCompleteDisplayFn(attributeName: string): string {
    return '';
  }

  async onOptionSelected(event: MatAutocompleteSelectedEvent): Promise<void> {
    const selectedValue = event.option.value;
    const attributes = await firstValueFrom(this.fullAttributes$$);

    const i = attributes.findIndex((a) => a.name == selectedValue);
    switch (attributes[i].valueType) {
      case ValueType.BOOL:
        attributes[i].value = true;
        break;
      case ValueType.URL:
      case ValueType.ENUM:
        attributes[i].value = '';
        break;
      case ValueType.REPEATED_ENUM:
        attributes[i].value = [];
        break;
      default:
        attributes[i].value = '';
        break;
    }

    this.updateAttributes(attributes);
    if (attributes[i].valueType === ValueType.REPEATED_ENUM) {
      this.form.addControl(selectedValue, this.createCheckboxFormGroup(attributes[i].valueDisplayNames, [], []));
    } else {
      this.form.addControl(selectedValue, this.fb.control(this.formStateWithDisabled(attributes[i].value)));
    }

    this.addToOrderedAttributes(attributes[i]);

    this.form.markAsDirty();
  }

  filterValues(search: string) {
    if (search.length > 0) {
      return this.fullAttributes$$.pipe(filter((attributes) => Array.isArray(attributes))).pipe(
        map((filteredAttributes) => {
          return filteredAttributes.filter(
            (attribute) => attribute.displayName.toLowerCase().indexOf(search.toLowerCase()) !== -1,
          );
        }),
      );
    } else {
      return this.fullAttributes$$;
    }
  }
  ngOnInit(): void {
    this.autocompleteControl = this.fb.control(this.formStateWithDisabled(''));
    // The metadata will contain all possible attributes the user could set. The attributes will only contain the
    // attributes that the user HAS set. Only the attributes are written to the backend. The metadata + attributes are
    // retrieved from the backend.
    // orderedInputAttributes contains the ordered list of attributes that the user HAS TO/CAN set.
    this.autocompleteControl.valueChanges.subscribe((newValue) => {
      this.filterValues(newValue).subscribe((filteredAttributes) => {
        this.filteredAttributes$$.next(filteredAttributes);
      });
    });
    combineLatest([this.metadata$, this.attributes$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([metadata, bingAttributes]) => {
        const attributes: BingAttributeFull[] = [];
        if (!bingAttributes) {
          bingAttributes = [];
        }
        metadata?.forEach((md) => {
          let value: unknown = undefined;

          const bingAttribute = bingAttributes.find((ga) => md.parent == ga.name);

          if (bingAttribute) {
            if (md.valueType === ValueType.REPEATED_ENUM) {
              this.form.addControl(
                bingAttribute.name,
                this.createCheckboxFormGroup(md.valueDisplayNames, md.values, bingAttribute.value),
              );
            }

            if (md.valueType === ValueType.ENUM) {
              const vdn = md.valueDisplayNames[md.values.findIndex((v) => v === bingAttribute.value)];
              this.form.addControl(bingAttribute.name, this.fb.control(this.formStateWithDisabled(vdn)));
            } else {
              this.form.addControl(
                bingAttribute.name,
                this.fb.control(this.formStateWithDisabled(bingAttribute.value)),
              );
            }

            const orderedInputAttribute: BingAttribute = {
              name: md.parent,
              value: value,
              toApiJson: () => ({}) as object,
            };

            this.addToOrderedAttributes(CreateAttribute(orderedInputAttribute, md));
          }

          if (this.form.controls[md.parent]) {
            value = this.form.controls[md.parent].value;
          }

          const attrib: BingAttribute = {
            name: md.parent,
            value: value,
            toApiJson: () => ({}) as object,
          };

          attributes.push(CreateAttribute(attrib, md));
        }); //end of forEach

        this.updateAttributes(attributes);
        this.sortOrderedAttributes();
      }); // end of subscribe
  }

  createCheckboxFormGroup(valueDisplayNames: string[], values: string[], setValues: string[]): UntypedFormGroup {
    const formGroup = new UntypedFormGroup({});
    valueDisplayNames?.forEach((vdn, index) => {
      let value = false;
      setValues.forEach((sv) => {
        if (values[index] === sv) {
          value = true;
        }
      });
      formGroup.addControl(vdn, this.fb.control(this.formStateWithDisabled(value)));
    });
    return formGroup;
  }

  updateAttributes(attributes: BingAttributeFull[]) {
    attributes.sort((a, b) => a.displayName.localeCompare(b.displayName));
    this.fullAttributes$$.next(attributes);
    this.filteredAttributes$$.next(attributes);
  }

  addToOrderedAttributes(attribute: BingAttributeFull) {
    firstValueFrom(this.orderedInputAttributes$$).then((orderedAttributes) => {
      orderedAttributes.unshift(attribute);
      this.orderedInputAttributes$$.next(orderedAttributes);
    });
  }

  sortOrderedAttributes() {
    firstValueFrom(this.orderedInputAttributes$$).then((orderedAttributes) => {
      orderedAttributes.sort((a, b) => a.displayName.localeCompare(b.displayName));
      this.orderedInputAttributes$$.next(orderedAttributes);
    });
  }

  removeFromOrderedAttributes(name: string) {
    firstValueFrom(this.orderedInputAttributes$$).then((orderedAttributes) => {
      orderedAttributes = orderedAttributes.filter((a) => a.name != name);
      this.orderedInputAttributes$$.next(orderedAttributes);
    });
  }

  private formStateWithDisabled(value?: any): object {
    return { value: value, disabled: this.editDisabled };
  }

  async clearAttribute(name) {
    const attributes = await firstValueFrom(this.fullAttributes$$);

    const i = attributes.findIndex((a) => a.name == name);

    attributes[i].value = null;

    this.removeFromOrderedAttributes(name);

    this.form.removeControl(name);
    this.updateAttributes(attributes);
    this.form.markAsDirty();
  }

  showLocationTab() {
    this.navigateToPrimaryInfoTab.emit();
  }
}
